
import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Space,
  Modal,
  Button,
  Form,
  Input,
  Row,
  Col,
  message,
  Select,
  InputNumber,
  DatePicker,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExperimentOutlined, SearchOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import moment from 'moment';

import {
  QaqcAnalysisService,
  CreateQaqcFibreAnalysisReq,
  QaqcLotReq
} from '@gtpl/shared-services/raw-material-procurement';

import { ProductService, SkuService } from '@gtpl/shared-services/masters';
import { QualityTypeEnum } from '@gtpl/shared-models/common-models';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';
import { Link } from 'react-router-dom';

const { Option } = Select;

interface ProductCountItem {
  productType: string;
  weightInGms: number;
  count: number;
  id?: number; // Add ID for edit mode
}

interface QAQCProductsCountProps {
  editRecord?: any;
  isEditMode?: boolean;
  onClose?: () => void;
}

const QAQCProductsCount: React.FC<QAQCProductsCountProps> = ({ editRecord, isEditMode = false, onClose }) => {
  const [lots, setLots] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<any>(null);

  const [productsData, setProductsData] = useState<any[]>([]);
  const [lotNumDropdownData, setLotNumDropdownData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
    const skucodeservice = new SkuService;
    
  // State for dynamic product count entries
  const [productCountEntries, setProductCountEntries] = useState<Array<{
    id?: number;
    processType: string;
    weightInGms: string;
    count: string;
  }>>([
    { processType: '', weightInGms: '', count: '' }
  ]);

  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();

  const qaqcService = new QaqcAnalysisService();
  const productService = new ProductService();

  useEffect(() => {
    fetchData();
    getAllProducts();
    getLotsDropdownForQAQC();
  }, []);

  useEffect(() => {
    if (isEditMode && editRecord) {
      // Auto-fill form when in edit mode
      setIsModalVisible(true);
      setSelectedRecord(editRecord);
      form.setFieldsValue({
        testDate: moment(editRecord.testDate),
      });

      // Auto-fill product count entries from items array with IDs
      if (editRecord.items && editRecord.items.length > 0) {
        const entries = editRecord.items.map((item: any) => ({
          id: item.id,
          processType: item.processType || '',
          weightInGms: item.weightInGms?.toString() || '',
          count: item.count?.toString() || '',
        }));
        setProductCountEntries(entries);
      }
    }
  }, [isEditMode, editRecord]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const req = new QaqcLotReq();

      if (filterForm.getFieldValue('variant')) {
        req.variant = filterForm.getFieldValue('variant');
      }

      if (filterForm.getFieldValue('quality')) {
        req.quality = filterForm.getFieldValue('quality');
      }

      if (filterForm.getFieldValue('lotNo')) {
        req.lotNumber = filterForm.getFieldValue('lotNo');
      }

      const res = await qaqcService.getLotsForProductCountsQAQC(req);

      if (res.status) {
        setLots(res.data);
      } else {
        AlertMessages.getErrorMessage(res.internalMessage);
        setLots([]);
      }
    } catch (error: any) {
      AlertMessages.getErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const onReset = () => {
    filterForm.resetFields();
    fetchData();
  };

  const openModal = (record: any) => {
    setSelectedRecord(record);
    setIsModalVisible(true);
    form.resetFields();
    setProductCountEntries([{ processType: '', weightInGms: '', count: '' }]);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedRecord(null);
    setProductCountEntries([{ processType: '', weightInGms: '', count: '' }]);
    if (onClose) onClose();
  };

  const addProductCountEntry = () => {
    setProductCountEntries([...productCountEntries, { processType: '', weightInGms: '', count: '' }]);
  };

  const removeProductCountEntry = (index: number) => {
    const newEntries = productCountEntries.filter((_, i) => i !== index);
    setProductCountEntries(newEntries);
  };

  const updateProductCountEntry = (index: number, field: string, value: string | number | null) => {
    const newEntries = [...productCountEntries];
    newEntries[index] = { ...newEntries[index], [field]: value?.toString() || '' };
    setProductCountEntries(newEntries);
  };


  const onFinish = async (values: any) => {
    try {
      // Validate that at least one entry has all required fields
      const validEntries = productCountEntries.filter(entry => 
        entry.processType && entry.weightInGms && entry.count
      );

      if (validEntries.length === 0) {
        AlertMessages.getErrorMessage('Please fill in at least one complete entry');
        return;
      }

      if (isEditMode && editRecord) {
        // Update mode - send array of records with their IDs
        const updatePayloads = validEntries.map(entry => ({
          id: entry.id,
          testDate: values.testDate,
          lotNumber: editRecord.lotNumber,
          operation: editRecord.operation || '',
          product: editRecord.product || '',
          quality: editRecord.quality || null,
          quantity: Number(editRecord.quantity) || 0,
          processType: entry.processType,
          weightInGms: Number(entry.weightInGms),
          count: Number(entry.count),
          productType: '',
          isQAQCApproved: editRecord.isQAQCApproved,
          is_qaqc_one_done: editRecord.is_qaqc_one_done || false,
          is_qaqc_two_done: editRecord.is_qaqc_two_done || false,
          is_qaqc_three_done: editRecord.is_qaqc_three_done || false,
        }));

        const res = await qaqcService.updateProductCounts(updatePayloads);

        if (res.status) {
          AlertMessages.getSuccessMessage('Product count updated successfully');
          closeModal();
          if (onClose) onClose();
          fetchData();
        } else {
          AlertMessages.getErrorMessage(res.internalMessage);
        }
      } else {
        // Create mode - send single payload with items array
        console.log(selectedRecord, 'selectedRecord');
const payload = {
  testDate: values.testDate,
  lotNumber: selectedRecord.lotNumber,
  operation: selectedRecord.operation || '',
  product: selectedRecord.skuCodeId || '',
  quality: selectedRecord.quality || null,
  quantity: Number(selectedRecord.physicalQuantity) || Number(selectedRecord.quantity) || 0,
  productType: '',
  isQAQCApproved: false,
  is_qaqc_one_done: false,
  is_qaqc_two_done: false,
  is_qaqc_three_done: false,

  items: validEntries.map(entry => ({
    processType: entry.processType,
    weightInGms: Number(entry.weightInGms),
    count: Number(entry.count),
  })),
};

const res = await qaqcService.createProductCounts(payload);

if (res.status) {
  AlertMessages.getSuccessMessage('Product counts created successfully');
  closeModal();
  fetchData();
} else {
  AlertMessages.getErrorMessage(res.internalMessage);
}
      }
    } catch (error: any) {
      AlertMessages.getErrorMessage(error.message);
    }
  };


  const getAllProducts= () =>{
  skucodeservice.getActiveSkuCodes().then(res =>{
    if(res.status){
      setProductsData(res.data);
    }else {
      if(res.intlCode){
        AlertMessages.getErrorMessage(res.internalMessage);
      }else{
        AlertMessages.getErrorMessage(res.internalMessage);
        setProductsData([])
      }
    }
  }).catch(err => {
    AlertMessages.getErrorMessage(err.message);
  })

}

  const getLotsDropdownForQAQC = () => {
    qaqcService.getLotsDropdownForQAQC().then(res => {
      if (res.status) setLotNumDropdownData(res.data);
      else setLotNumDropdownData([]);
    });
  };

const handleLotTableChange = (page: number, pageSize?: number) => {
  setCurrentPage(page);
  setCurrentPageSize(pageSize || 10);
};

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleResetSearch = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };
  function handleReset(clearFilters) {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : false,
    onFilterDropdownVisibleChange: visible => {
      if (visible) { setTimeout(() => searchInput.current.select()); }
    },
    render: text =>
      text ? (
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text.toString()}
          />
        ) : text
      ) : null
  });

  const lotColumns: ColumnsType<any> = [
    {
      title: 'S No',
      key: 'sno',
      width: 70,
      render: (_text, _record, index) => (index + 1) + ((currentPage - 1) * currentPageSize),
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
       sorter: (a, b) => (a?.lotNumber || '')?.localeCompare(b?.lotNumber || ''),

      ...getColumnSearchProps('lotNumber'),
    },
    {
      title: 'Operation',
      dataIndex: 'operation',
      sorter: (a, b) => (a?.operation || '')?.localeCompare(b?.operation || ''),
      ...getColumnSearchProps('operation'),
    },
    {
      title: 'Prev Operation',
      dataIndex: 'prevOperation',
      sorter: (a, b) => (a?.prevOperation || '')?.localeCompare(b?.prevOperation || ''),
      ...getColumnSearchProps('prevOperation'),
    },
    {
      title: 'Variant',
      dataIndex: 'product',
      sorter: (a, b) => (a?.product || '')?.localeCompare(b?.product || ''),
      ...getColumnSearchProps('product'),
    },
    {
      title: 'Quality',
      dataIndex: 'quality',
      sorter: (a, b) => (a?.quality || '')?.localeCompare(b?.quality || ''),
      ...getColumnSearchProps('quality'),
    },
    {
      title: 'Quantity',
      dataIndex: 'physicalQuantity',
      sorter: (a, b) => (a?.physicalQuantity || '')?.localeCompare(b?.physicalQuantity || ''),
      
      ...getColumnSearchProps('physicalQuantity'),
      render: (value) => value ? Number(Number(value).toFixed(2)) : '-',
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Space>
          <ExperimentOutlined
            style={{ color: '#52c41a', cursor: 'pointer' }}
            onClick={() => openModal(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <Card 
      title="Product Counts" 
      style={{ margin: 20 }}
      headStyle={{
        backgroundColor: '#69c0ff',
        border: 0,
        textAlign: 'center',
      }}
      extra={
        !isEditMode && (
          <Link to="/qaqc-products-count-view">
            <span style={{ color: 'white' }}>
              <Button type='primary'>View</Button>
            </span>
          </Link>
        )
      }
    >
      {!isEditMode && (
        <>
          <Form form={filterForm} onFinish={fetchData} layout="inline" style={{ marginBottom: 16 }}>
            <Row gutter={16} style={{ width: '100%' }}>
              <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                <Form.Item name="variant" label="Variant">
                  <Select 
                    allowClear
                    showSearch
                    placeholder='Select Variant'
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {productsData.map(p => (
                      <Option key={p.skuCodeId} value={p.skuCodeId}>
                        {p.shortCode}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                <Form.Item name="lotNo" label="Lot No">
                  <Select 
                    allowClear
                    showSearch
                    placeholder='Select Lot No'
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {lotNumDropdownData.map(l => (
                      <Option key={l.lotNumber} value={l.lotNumber}>
                        {l.lotNumber}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={{ span: 6 }} sm={{ span: 6 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                <Form.Item name="quality" label="Quality">
                  <Select 
                    allowClear
                    showSearch
                    placeholder='Select Quality'
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {Object.values(QualityTypeEnum).map(q => (
                      <Option key={q} value={q}>{q}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
           
              <Col style={{ textAlign: 'right', paddingTop: '0px' }}>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
                <Button htmlType="button" style={{ margin: '0 14px' }} onClick={onReset}>
                  Reset
                </Button>
              </Col>
            </Row>
          </Form>
          <Table
            columns={lotColumns}
            dataSource={lots}
            loading={loading}
            rowKey="productionInventoryId"
            pagination={{
              current: currentPage,
              pageSize: currentPageSize,
            }}
            onChange={(pagination) => {
              setCurrentPage(pagination.current || 1);
              setCurrentPageSize(pagination.pageSize || 10);
            }}

          />
        </>
      )}

      {!isEditMode && (
        <Modal
          visible={isModalVisible}
          onCancel={closeModal}
          footer={null}
          width={900}
          title={
            <div
              style={{
                background: '#69c0ff',
                padding: '10px 16px',
                margin: '-24px -24px 0 -24px',
                borderRadius: '8px 8px 0 0',
                fontWeight: 600,
                color: '#fff',
              }}
            >
              Product Counts Entry
            </div>
          }
          bodyStyle={{ padding: '24px' }}
        >
          {selectedRecord && (
            <div style={{ marginBottom: 24, padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
              <Row gutter={16}>
                <Col span={12}><b>Lot Number:</b> {selectedRecord.lotNumber}</Col>
                <Col span={12}><b>Product:</b> {selectedRecord.product}</Col>
              </Row>
            </div>
          )}

          <Form form={form} onFinish={onFinish} layout="vertical">
            <Form.Item 
              name="testDate" 
              label="Test Date" 
              rules={[{ required: true, message: 'Please select test date' }]}
            >
              <DatePicker style={{ width: '40%' }} />
            </Form.Item>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h4 style={{ margin: 0 }}>Product Count Entries</h4>
                <Button 
                  type="primary" 
                  size="small" 
                  icon={<PlusOutlined />}
                  onClick={addProductCountEntry}
                >
                  Add Entry
                </Button>
              </div>
              
              {productCountEntries.map((entry, index) => (
                <div key={index} style={{ marginBottom: 12, padding: '16px', border: '1px solid #d9d9d9', borderRadius: '8px', background: '#fff', position: 'relative' }}>
                  <Row gutter={16} align="middle">
                    <Col span={7}>
                      <Form.Item label="Process Type" required style={{ marginBottom: 8 }}>
                        <Input
                          value={entry.processType}
                          onChange={(e) => updateProductCountEntry(index, 'processType', e.target.value)}
                          placeholder="Process Type"
                          size="small"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item label="Weight (gms)" required style={{ marginBottom: 8 }}>
                        <InputNumber
                          value={entry.weightInGms ? Number(entry.weightInGms) : undefined}
                          onChange={(value) => updateProductCountEntry(index, 'weightInGms', value)}
                          placeholder="Weight"
                          style={{ width: '100%' }}
                          min={0}
                          size="small"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item label="Count" required style={{ marginBottom: 8 }}>
                        <InputNumber
                          value={entry.count ? Number(entry.count) : undefined}
                          onChange={(value) => updateProductCountEntry(index, 'count', value)}
                          placeholder="Count"
                          style={{ width: '100%' }}
                          min={0}
                          size="small"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={3} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      {productCountEntries.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeProductCountEntry(index)}
                          size="small"
                        />
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
            </div>

            <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
              <Space>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
                <Button onClick={closeModal}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {isEditMode && (
        <div style={{ padding: '24px' }}>
          {selectedRecord && (
            <div style={{ marginBottom: 24, padding: '16px', background: '#f5f5f5', borderRadius: '8px' }}>
              <Row gutter={16}>
                <Col span={6}><b>Lot Number:</b> {selectedRecord.lotNumber}</Col>
                <Col span={6}><b>Operation:</b> {selectedRecord.operation}</Col>
                <Col span={6}><b>Product:</b> {selectedRecord.product}</Col>
                <Col span={6}><b>Quality:</b> {selectedRecord.quality || '-'}</Col>
              </Row>
            </div>
          )}

          <Form form={form} onFinish={onFinish} layout="vertical">
            <Form.Item 
              name="testDate" 
              label="Test Date" 
              rules={[{ required: true, message: 'Please select test date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: 0, marginBottom: 12 }}>Product Count Entries</h4>
              
              {productCountEntries.map((entry, index) => (
                <div key={index} style={{ marginBottom: 12, padding: '16px', border: '1px solid #d9d9d9', borderRadius: '8px', background: '#fff', position: 'relative' }}>
                  <Row gutter={16} align="middle">
                    <Col span={8}>
                      <Form.Item label="Process Type" required style={{ marginBottom: 8 }}>
                        <Input
                          value={entry.processType}
                          onChange={(e) => updateProductCountEntry(index, 'processType', e.target.value)}
                          placeholder="Process Type"
                          size="small"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Weight (gms)" required style={{ marginBottom: 8 }}>
                        <InputNumber
                          value={entry.weightInGms ? Number(entry.weightInGms) : undefined}
                          onChange={(value) => updateProductCountEntry(index, 'weightInGms', value)}
                          placeholder="Weight"
                          style={{ width: '100%' }}
                          min={0}
                          size="small"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label="Count" required style={{ marginBottom: 8 }}>
                        <InputNumber
                          value={entry.count ? Number(entry.count) : undefined}
                          onChange={(value) => updateProductCountEntry(index, 'count', value)}
                          placeholder="Count"
                          style={{ width: '100%' }}
                          min={0}
                          size="small"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>

            <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
              <Space>
                <Button type="primary" htmlType="submit">
                  Update
                </Button>
                <Button onClick={closeModal}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      )}
    </Card>
  );
};

export default QAQCProductsCount;

